
import Fields from './Fields';
import React from 'react';

import { useState, useEffect, useRef } from 'react'
import Registry from './Registry';

const FieldsTable = ({ values, fields, deleteValue, labelLookup }) => {

    if (values.length === 0) {
        return null;
    }

    // Helper function to get display value using lookup dictionary
    const getDisplayValue = (fieldName, storedValue) => {        
        // Fallback to the stored value (backward compatibility)
        return labelLookup?.[fieldName]?.[storedValue] || storedValue;
    };

    return (
        <table className='repeater-values-table'>
            <thead>
                <tr>
                    {
                        fields.map((field) => <th key={field.name}>{field.label}</th>).concat([<th key="actions"></th>])
                    }
                </tr>
            </thead>
            <tbody>
                {values.map((v, idx) => {
                    const cells = fields.map(({ name }) => {
                        const displayValue = getDisplayValue(name, v[name]);
                        return <td key={name}>{displayValue}</td>;
                    });
                    cells.push(<td key="actions"><button className="delete-values-table" key={`delete-value-button-${idx}`} onClick={() => deleteValue(idx)}>-</button></td>);
                    return (
                        <tr key={idx}>
                            {cells}
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

const EMPTY_LOOKUP = {};
const Repeater = ({
    value = [],
    fields,
    name,
    label,
    onChange,
    // using external reference to prevent infinite re-renders when labelLookup is undefined
    labelLookupPrefill = EMPTY_LOOKUP,
    duplicateErrorMessage = 'This combination of values already exists'
}) => {
    const [currentValue, setCurrentValue] = useState({});
    const [labelLookup, setLabelLookup] = useState(labelLookupPrefill);
    const [fieldKey, setFieldKey] = useState(0);
    const [duplicateError, setDuplicateError] = useState(false);
    
    // Use ref to store runtime additions that persist across re-renders
    const runtimeLookupRef = useRef({});
    
    // Build complete lookup dictionary from fields, prefill, and runtime additions
    useEffect(() => {
        const newLookup = {};
        
        // Start with prefill data
        Object.keys(labelLookupPrefill).forEach(fieldName => {
            newLookup[fieldName] = { ...labelLookupPrefill[fieldName] };
        });
        
        // Add field options
        fields.forEach(field => {
            if (field.getOptionLabel && field.options) {
                if (!newLookup[field.name]) {
                    newLookup[field.name] = {};
                }
                
                field.options.forEach(option => {
                    try {
                        const optionValue = typeof option === 'object' ? option.id || option.value : option;
                        const label = field.getOptionLabel(option);
                        
                        if (label && label !== optionValue) {
                            newLookup[field.name][optionValue] = label;
                        }
                    } catch (error) {
                        console.warn('Failed to build lookup for option:', field.name, option, error);
                    }
                });
            }
        });
        
        // Add runtime additions
        Object.keys(runtimeLookupRef.current).forEach(fieldName => {
            if (!newLookup[fieldName]) {
                newLookup[fieldName] = {};
            }
            newLookup[fieldName] = {
                ...newLookup[fieldName],
                ...runtimeLookupRef.current[fieldName]
            };
        });
        
        setLabelLookup(newLookup);
    }, [fields, labelLookupPrefill]);

    const onChangeFields = (e) => {
        const fieldName = e.target.name;
        const fieldValue = e.target.value;

        // Clear duplicate error when user changes any field
        if (duplicateError) {
            setDuplicateError(false);
        }

        // Check if we received a full object (AsyncAutoComplete with returnFullObject=true)
        if (fieldValue && typeof fieldValue === 'object') {
            const actualValue = fieldValue.value || fieldValue.id; // Extract the ID
            const label = fieldValue.label;
            
            if (label && label !== actualValue) {
                // Store in ref for persistence across re-renders
                runtimeLookupRef.current = {
                    ...runtimeLookupRef.current,
                    [fieldName]: {
                        ...runtimeLookupRef.current[fieldName],
                        [actualValue]: label
                    }
                };
                
                // Also update state for immediate UI update
                setLabelLookup(prev => ({
                    ...prev,
                    [fieldName]: {
                        ...prev[fieldName],
                        [actualValue]: label
                    }
                }));
            }
        }
        // Note: For regular AutoComplete fields, lookup is built on initialization from options array
        
        // Store the actual value (ID) in currentValue, not the full object
        const valueToStore = (fieldValue && typeof fieldValue === 'object') 
            ? (fieldValue.value || fieldValue.id) 
            : fieldValue;
            
        const updatedValue = {
            ...currentValue,
            [fieldName]: valueToStore,
        }
        return setCurrentValue(updatedValue);
    }

    const deleteValue = (indexToDelete) => {
        const updatedValues = value.filter((v, idx) => idx !== indexToDelete);
        return onChange({
            target: {
                value: updatedValues,
                name
            },
        });
    }

    // Check if the current combination of values already exists
    const isDuplicate = (newValue) => {
        if (!value) return false;
        return value.some((existingValue) => {
            // Compare all field values
            return fields.every(field => {
                const fieldName = field.name;
                const newFieldValue = newValue[fieldName];
                const existingFieldValue = existingValue[fieldName];
                
                // Handle null/undefined as equivalent
                if ((newFieldValue === null || newFieldValue === undefined || newFieldValue === '') &&
                    (existingFieldValue === null || existingFieldValue === undefined || existingFieldValue === '')) {
                    return true;
                }
                
                // Compare values
                return newFieldValue === existingFieldValue;
            });
        });
    }

    const requiredFields = fields.filter(field => field.required);
    const allRequiredFilled = requiredFields.every(field => {
        const v = currentValue[field.name];
        return v !== null && v !== undefined && v !== '';
    });
    const hasAnyValue = Object.values(currentValue).some(v => v !== null && v !== undefined && v !== '');
    const hasValue = requiredFields.length > 0 ? allRequiredFilled : hasAnyValue;
    const isDuplicateValue = hasValue && isDuplicate(currentValue);
    
    const addValue = (e) => {
        e.preventDefault();
        
        // Check for duplicate before adding
        if (isDuplicate(currentValue)) {
            setDuplicateError(true);
            // Clear error after 3 seconds
            setTimeout(() => setDuplicateError(false), 3000);
            return;
        }
        
        setDuplicateError(false);
        
        // Reset currentValue to undefined for all fields (simpler and more consistent)
        const newCurrentValue = fields.reduce((cv, field) => {
            return Object.assign(cv, { [field.name]: undefined })
        }, {})
        
        
        setCurrentValue(newCurrentValue);
        setFieldKey(prev => prev + 1); // Force re-render of fields
        return onChange({
            target: { name, value: [...value, currentValue] }
        });

    }

    return (
        <div className="repeater-wrapper">
            <div className='repeater-label'>
                {label}
            </div>
            <FieldsTable fields={fields} values={value} deleteValue={deleteValue} labelLookup={labelLookup} />
            <form onSubmit={addValue}>
                <Fields 
                    key={fieldKey} 
                    fields={fields.map(field => ({
                        ...field,
                        ...(field.type === 'AsyncAutoComplete' && { returnFullObject: true }),
                        ...(isDuplicateValue && { error: true, helperText: duplicateErrorMessage })
                    }))} 
                    onChange={onChangeFields} 
                    values={currentValue}
                />
                <div className='repeater-actions'>
                    <button onClick={addValue} onSubmit={addValue} className='repeater-actions-add' disabled={!hasValue || isDuplicateValue}> + </button>
                </div>
            </form>
        </div>

    )
}

Registry.addField('repeater', Repeater);
export default Repeater;